import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowUndoFilled, SaveRegular } from '@fluentui/react-icons';
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBlocker, useParams } from 'react-router-dom';

import { PathsSelectorFrom } from '@tdp/common/form';
import { useCommand } from '@tdp/core';

import { IAppDetails } from '../../../abstractions';
import { ChangeRevertConfirmationDialog } from '../../../components/ChangeRevertConfirmationDialog';
import {
  createAppEditsHook,
  createAppRevertHook,
  createAppZoomHook,
} from '../../../hooks';

const useAppLens = createAppZoomHook((app) => app);

const useStyles = makeStyles({
  footer: {
    height: '100px', //TODO(nibeauli): check with Jitish about height
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground2,
    //borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXXXL, //TODO(nibeauli): design calls for 44px padding but that is 8px larger than largest spacing token
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
    display: 'flex',
    alignItems: 'center',
    zIndex: 2, // push this above so boxshadow works
    //TODO(nibeauli): factor this out as a custom token
    boxShadow: '0 0 2px rgba(0, 0, 0, 0.12), 0 -2px 4px rgba(0, 0, 0, 0.14)', //tokens.shadow 4 only does 2px downward not upward box shadow.
  },
});

export function createAppFeaturesFooter(paths: PathsSelectorFrom<IAppDetails>) {
  const useEditTracking = createAppEditsHook(paths);
  const useRevert = createAppRevertHook(paths);

  return function AppDetailsFormLayout({ isError, onRevertCallback }: { isError?: boolean, onRevertCallback?: () => void }) {
    const classes = useStyles();
    const formId = useId();
    const appId = useParams<{ appId: string }>().appId ?? '';
    const intl = useIntl();

    const [hasEdits] = useEditTracking(appId);
    const writeApp = useCommand(
      (commands) => commands.appDetailCommands.writeApp
    );
    const formLens = useAppLens(appId);
    const [revertConfirmDialogOpen, setRevertConfirmDialogOpen] =
      useState(false);

    const onSave: MouseEventHandler<typeof Button> = useCallback(
      (evt) => {
        // don't let the form submit occur and trigger a navigation event
        evt.preventDefault();
        writeApp({
          appDetails: formLens.current,
        });
      },
      [writeApp, formLens.current]
    );

    const onRevert = useRevert(appId);
    const revertConfirmationHandler = useCallback(() => {
      setRevertConfirmDialogOpen(false);
      onRevert();
      onRevertCallback?.();
    }, [onRevert]);

    const dismisssRevertConfirmationDialog = useCallback(() => {
      setRevertConfirmDialogOpen(false);
    }, []);

    const openRevertConfirmationDialog = useCallback(() => {
      setRevertConfirmDialogOpen(true);
    }, []);

    //TODO(hook navigation events as well to avoid navigation out of the TDP app)
    const blockerState = useBlocker(hasEdits);
    useEffect(() => {
      if (blockerState.state === 'blocked') {
        const test = confirm(
          intl.formatMessage({
            defaultMessage:
              'You have unsaved changes, please save or discard them before navigating away.',
            description:
              'Confirmation message when user tries to navigate away with unsaved changes.',
          })
        );
        if (test) {
          blockerState.proceed();
        } else {
          blockerState.reset();
        }
      }
    }, [blockerState]);

    return (
      <>
        <ChangeRevertConfirmationDialog
          onConfirm={revertConfirmationHandler}
          onCancel={dismisssRevertConfirmationDialog}
          isOpen={revertConfirmDialogOpen}
        />

        <div className={classes.footer}>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={!hasEdits || !!isError}
            onClick={onSave}
            form={formId}
            type="submit"
          >
            <FormattedMessage defaultMessage="Save" />
          </Button>
          <Button
            icon={<ArrowUndoFilled />}
            disabled={!hasEdits}
            onClick={openRevertConfirmationDialog}
          >
            <FormattedMessage defaultMessage="Revert" />
          </Button>
        </div>
      </>
    );
  };
}
